/* Calcolo delle posizioni — porting fedele di flusso-urgenze.html (816-1020).
   Ogni evento riceve una y. In modalità "nascondi" gli elementi filtrati
   consumano altezza 0, così barre/riquadri/fasi si ricompattano in un solo
   passaggio. Le posizioni orizzontali derivano dall'indice di colonna. */
import { colX, estimateMeasure, F_NOTE, L, type Measure } from "./constants.js";
import { plain } from "./markup.js";
import type {
  DiagramState,
  Frame,
  LayoutBar,
  LayoutOut,
  LayoutSeg,
  Narrow,
  PEvent,
  PRow,
  PreparedModel,
} from "./model.js";
import { anyFilter, noteVisible } from "./visible.js";

interface OpenBar {
  a: string;
  depth: number;
  y0: number;
  openedAt: number;
  key: number;
  y1?: number;
}


/** Spezza per caratteri una parola più larga della riga: un URL lunghissimo
    non ha spazi su cui andare a capo, e senza questo sborderebbe comunque. */
function breakWord(word: string, limit: number, measure: Measure): string[] {
  const parts: string[] = [];
  let current = "";
  for (const ch of word) {
    const candidate = current + ch;
    if (current && measure(candidate, F_NOTE) > limit) {
      parts.push(current);
      current = ch;
    } else {
      current = candidate;
    }
  }
  if (current) parts.push(current);
  return parts;
}

/** Manda a capo sulle parole una riga che supera `limit`. Chi ci sta già
    torna intatto, così i diagrammi esistenti non cambiano di una virgola. */
function wrapLine(line: string, limit: number, measure: Measure): string[] {
  if (limit <= 0 || measure(line, F_NOTE) <= limit) return [line];
  const out: string[] = [];
  let current = "";
  const flush = (): void => {
    if (current) out.push(current);
    current = "";
  };
  for (const word of line.split(" ")) {
    if (measure(word, F_NOTE) > limit) {
      // la parola da sola non ci sta: chiudo la riga corrente e la spezzo
      flush();
      const parts = breakWord(word, limit, measure);
      out.push(...parts.slice(0, -1));
      current = parts[parts.length - 1] ?? "";
      continue;
    }
    const candidate = current ? `${current} ${word}` : word;
    if (current && measure(candidate, F_NOTE) > limit) {
      flush();
      current = word;
    } else {
      current = candidate;
    }
  }
  flush();
  return out;
}

export function computeLayout(
  model: PreparedModel,
  S: DiagramState,
  vis: Set<number>,
  measure: Measure = estimateMeasure,
): LayoutOut {
  const { PHASES, BLOCKS, events, width: W } = model;
  const px = (id: string): number => colX(model.partIdx.get(id) ?? 0);

  const hide = S.mode === "hide";
  const noFilter = !anyFilter(S);
  const show = (m: PEvent): boolean => noFilter || vis.has(m.n!);
  const collapsed = S.collapsed ?? new Set<string>();

  const inClosed = (ev: PEvent): boolean => !!(ev.phase && collapsed.has(ev.phase.id));
  const rowVisible = (r: PRow): boolean =>
    r.kind === "note" ? noFilter || noteVisible(r.ev!, S) : r.msgs.some(show);
  const rowDrawn = (r: PRow): boolean =>
    !inClosed(r.kind === "note" ? r.ev! : r.msgs[0]!) && (!hide || rowVisible(r));

  /* visibilità di blocchi/rami: risalgono dai messaggi contenuti */
  for (const b of BLOCKS) {
    for (const br of b.branches) {
      br.visible = br.msgs.some((m) => !inClosed(m)) && (!hide || br.msgs.some(show));
    }
    b.visible = b.branches.some((br) => br.visible);
    b.firstVisibleBranch = b.branches.find((br) => br.visible) ?? b.branches[0];
  }
  for (const p of PHASES) {
    p.visible = !hide || p.msgs.some(show);
    p.closed = collapsed.has(p.id);
  }

  const out: LayoutOut = {
    rows: [],
    bars: [],
    frames: [],
    notes: [],
    phases: [],
    segs: [],
    height: 0,
  };
  let y = 14;
  let lastRowY: number | null = null;
  let seqMsg = 0;
  let pendingCreate: PEvent | null = null;
  const actStack: Record<string, OpenBar[]> = {};
  for (const p of model.doc.participants) actStack[p.id] = [];
  const segOpen: Record<string, LayoutSeg | null> = {};
  const frameStack: (Frame | null)[] = [];

  function openSeg(ev: PEvent, boxY: number, dimmed: boolean, suppressed: boolean): void {
    const a = (ev.raw as Narrow<"create">).a;
    segOpen[a] = {
      a,
      boxY,
      y0: boxY + L.boxH / 2,
      key: ev.i,
      inline: !ev.standalone,
      dim: dimmed,
      suppressed,
    };
  }

  for (const ev of events) {
    switch (ev.raw.t) {
      case "phase": {
        const p = ev.phase!;
        if (!p.visible) {
          p.y = y;
          break;
        }
        p.y = y;
        out.phases.push({ p, y: y + L.phaseH / 2 - 6 });
        y += L.phaseH + (p.closed ? 8 : 0);
        break;
      }

      case "note": {
        const r = ev.row!;
        if (!rowDrawn(r)) {
          r._y = y;
          break;
        }
        const raw = ev.raw as Narrow<"note">;
        // Il testo si spezza sugli a-capo espliciti; le righe che comunque non
        // ci starebbero vengono mandate a capo sulle parole, altrimenti la nota
        // sborda dal disegno e il testo viene tagliato.
        const maxTextW = W - (L.gutterX + 20) - 12 - L.notePadX * 2;
        const lines = plain(raw.text)
          .split("\n")
          .flatMap((line) => wrapLine(line, maxTextW, measure));
        const w = Math.max(...lines.map((t) => measure(t, F_NOTE))) + L.notePadX * 2;
        const h = lines.length * L.noteLine + L.notePadY * 2;
        const cx = px(raw.over);
        // Una nota larga, centrata sul primo (o sull'ultimo) attore, sfora il
        // margine e finisce sotto la colonna dei numeri di riga: il testo
        // diventa illeggibile. La si trattiene dentro l'area disegnabile.
        const minX = L.gutterX + 20; // la banda dei numeri arriva a gutterX + 14
        const maxX = Math.max(minX, W - 12 - w);
        const x = Math.min(Math.max(cx - w / 2, minX), maxX);
        out.notes.push({ ev, x, y: y + 6, w, h, lines, dim: !rowVisible(r) });
        y += h + 16;
        break;
      }

      case "block": {
        const b = ev.block!;
        if (!b.visible) {
          b.y0 = y;
          frameStack.push(null);
          break;
        }
        const f: Frame = { b, y0: y, dividers: [], branchY: [] };
        out.frames.push(f);
        frameStack.push(f);
        b.frame = f;
        if (b.firstVisibleBranch === b.branches[0]) y += L.blockHead;
        f.branchY.push({ br: b.branches[0]!, y, head: b.firstVisibleBranch === b.branches[0] });
        break;
      }
      case "else": {
        const b = ev.block!;
        const br = ev.branch!;
        if (!b.visible || !br.visible) break;
        const f = b.frame!;
        if (br === b.firstVisibleBranch) {
          y += L.blockHead;
          f.branchY.push({ br, y, head: true });
        } else {
          y += 6;
          f.dividers.push(y);
          y += L.branchHead;
          f.branchY.push({ br, y, head: false });
        }
        break;
      }
      case "end": {
        const f = frameStack.pop();
        if (f) {
          y += L.blockPad;
          f.y1 = y;
          y += 12;
        }
        break;
      }

      case "msg": {
        const r = ev.row!;
        /* membro successivo di un gruppo contemporaneo: riusa la y della riga. */
        if (r.msgs[0] !== ev) {
          ev.y = r._y;
          if (pendingCreate) {
            openSeg(pendingCreate, r._y!, !r._on, !r._drawn);
            pendingCreate = null;
          }
          break;
        }
        const on = rowVisible(r);
        const isDrawn = rowDrawn(r);
        r._on = on;
        r._drawn = isDrawn;
        if (!isDrawn) {
          r._y = y;
          ev.y = y;
          if (pendingCreate) {
            openSeg(pendingCreate, y, true, true);
            pendingCreate = null;
          }
          break;
        }
        const h =
          (r.sync ? L.rowSync : L.row) + (r.hasCreate ? 30 : 0) + (r.hasDestroy ? 20 : 0);
        const yc = y + (r.sync ? L.rowSync : L.row) / 2 + (r.hasCreate ? 15 : 0);
        r._y = yc;
        ev.y = yc;
        y += h;
        lastRowY = yc;
        /* numerazione: stabile in "ombreggia", rinumerata sui visibili in "nascondi" */
        const shownMsgs = hide ? r.msgs.filter(show) : r.msgs;
        let label: string;
        if (hide && !noFilter) {
          const a = seqMsg + 1;
          seqMsg += shownMsgs.length;
          label = shownMsgs.length > 1 ? a + "–" + seqMsg : String(a);
        } else {
          label =
            r.msgs.length > 1
              ? r.msgs[0]!.n + "–" + r.msgs[r.msgs.length - 1]!.n
              : String(r.msgs[0]!.n);
        }
        out.rows.push({ kind: "msg", row: r, msgs: r.msgs, sync: r.sync, y: yc, dim: !on, label });
        if (pendingCreate) {
          openSeg(pendingCreate, yc, !on, false);
          pendingCreate = null;
        }
        break;
      }

      case "create": {
        if (!ev.standalone) {
          pendingCreate = ev;
          break;
        }
        const drawnHere = ev.row ? rowDrawn(ev.row) : true;
        if (!drawnHere) {
          openSeg(ev, y, true, true);
          break;
        }
        const boxY = y + L.boxH / 2;
        y += L.boxH + 10;
        openSeg(ev, boxY, ev.row ? !rowVisible(ev.row) : false, false);
        break;
      }
      case "destroy": {
        const a = (ev.raw as Narrow<"destroy">).a;
        const s = segOpen[a];
        segOpen[a] = null;
        if (!s) break;
        const r = ev.row;
        const yb =
          r && r._y != null ? r._y + 26 : lastRowY != null ? lastRowY + 22 : y;
        s.y1 = Math.max(yb, s.y0 + 20);
        if (!s.suppressed) out.segs.push(s);
        break;
      }

      /* Stack di attivazione per attore: `depth` sfalsa le barre annidate. */
      case "act": {
        const a = (ev.raw as Narrow<"act">).a;
        const st = actStack[a]!;
        st.push({
          a,
          depth: st.length,
          y0: lastRowY != null ? lastRowY : y,
          openedAt: out.rows.length,
          key: ev.i,
        });
        break;
      }
      case "deact": {
        const a = (ev.raw as Narrow<"deact">).a;
        const st = actStack[a]!;
        const bar = st.pop();
        if (!bar) break;
        bar.y1 = (lastRowY != null ? lastRowY : y) + 9;
        if (out.rows.length > bar.openedAt && bar.y1 - bar.y0 >= 4) {
          out.bars.push({
            a: bar.a,
            depth: bar.depth,
            y0: bar.y0,
            y1: bar.y1,
            openedAt: bar.openedAt,
            key: bar.key,
          } satisfies LayoutBar);
        }
        break;
      }
    }
  }

  /* segmenti eventualmente ancora aperti */
  for (const k of Object.keys(segOpen)) {
    const s = segOpen[k];
    if (s && !s.suppressed) {
      s.y1 = y;
      out.segs.push(s);
    }
  }

  out.height = y + 30;

  /* estensione orizzontale dei riquadri: concentrici rientrando nel padre */
  const byDepth = out.frames.slice().sort((a, b) => a.b.depth - b.b.depth);
  const bounds = new Map<Frame, { x0: number; x1: number }>();
  const findParent = (f: Frame): Frame | null => {
    let best: Frame | null = null;
    for (const g of out.frames) {
      if (g === f || g.b.depth >= f.b.depth) continue;
      if (g.y0 <= f.y0 && (g.y1 ?? 1e9) >= (f.y1 ?? f.y0))
        if (!best || g.b.depth > best.b.depth) best = g;
    }
    return best;
  };
  for (const f of byDepth) {
    const cols = [...f.b.cols];
    if (!cols.length) {
      f.x0 = L.marginL - 40;
      f.x1 = W - 24;
      bounds.set(f, { x0: f.x0, x1: f.x1 });
      continue;
    }
    let x0 = colX(Math.min(...cols)) - 78;
    let x1 = colX(Math.max(...cols)) + 78;
    const parent = findParent(f);
    if (parent && bounds.has(parent)) {
      const pb = bounds.get(parent)!;
      x0 = Math.max(x0, pb.x0 + L.blockInset);
      x1 = Math.min(x1, pb.x1 - L.blockInset);
    } else {
      x0 = Math.max(x0, L.marginL - 40);
      x1 = Math.min(x1, W - 24);
    }
    f.x0 = x0;
    f.x1 = x1;
    bounds.set(f, { x0, x1 });
  }

  return out;
}
